/**
 * One place that owns the authed-request retry policy for every dashboard
 * fetch wrapper / raw client:
 *
 *   run request →
 *     · 401 (any reason)                → refresh ONCE (globally deduped) → retry ONCE
 *     · 403 workspace auth blips        → refresh the token (re-mint roles) → retry w/ backoff
 *       (`workspace_roles_required`, `WORKSPACE_ACCESS_DENIED`)
 *   → never surface a transient auth/RBAC blip to the UI.
 *
 * Callers pass a `buildRequest` thunk (so the request can be re-issued) and
 * keep their own body parsing / 204 / FormData handling on the returned
 * Response. This guarantees a raw 401 / roles-blip is never surfaced and that N
 * concurrent failures share exactly one refresh.
 */

import { refreshDashboardSession } from "@/lib/authRefreshCoordinator";
import {
  dispatchSessionExpired,
  RefreshInvalidError,
  SessionExpiredError,
} from "@/lib/sessionExpiredEvent";

function abortError() {
  const error = new Error("Request aborted");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal) {
  if (signal?.aborted) throw abortError();
}

function abortableDelay(ms, signal) {
  throwIfAborted(signal);
  if (!signal) return new Promise((resolve) => setTimeout(resolve, ms));
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError());
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

// Backoff for re-issuing after a roles-blip refresh (covers Keycloak role
// propagation lag / a lingering Kong introspection blip).
const ROLES_BLIP_RETRY_DELAYS_MS = [500, 1200, 2500];

// Recoverable 403 workspace-auth blips: stale token right after a workspace
// switch / freshly-granted roles, or a transient Kong introspection lag where
// resource_access / azp has not caught up yet. Not a real denial (the route
// guard already authorized the page).
const RECOVERABLE_WORKSPACE_AUTH_ERRORS = new Set([
  "workspace_roles_required",
  "workspace_access_denied",
]);

async function isRecoverableWorkspaceAuthBlip(res) {
  if (!res || res.status !== 403) return false;
  try {
    const data = await res.clone().json();
    const code = data?.detail?.error ?? data?.error;
    if (typeof code !== "string") return false;
    return RECOVERABLE_WORKSPACE_AUTH_ERRORS.has(code.trim().toLowerCase());
  } catch {
    return false;
  }
}

// Single, globally-deduped refresh. Rethrows a DEFINITIVE expiry as
// SessionExpiredError (after dispatching the modal); returns false on a
// transient refresh failure (429 / 5xx / network / cooldown).
async function tryRefreshSession() {
  try {
    await refreshDashboardSession();
    return true;
  } catch (error) {
    if (error instanceof RefreshInvalidError) {
      dispatchSessionExpired();
      throw new SessionExpiredError();
    }
    return false;
  }
}

/**
 * @param {() => Promise<Response>} buildRequest - builds & sends the request; re-invokable for retry.
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<Response>} the final Response (caller reads the body).
 */
export async function fetchWithAuthRetry(buildRequest, { signal } = {}) {
  throwIfAborted(signal);
  let res = await buildRequest();

  // First 401 (any reason): attempt a single, globally-deduped refresh, then
  // retry once. A transient refresh failure keeps the session and returns the
  // original 401 (callers swallow it; the next call recovers).
  if (res.status === 401) {
    const firstRes = res;
    if (!(await tryRefreshSession())) return firstRes;
    throwIfAborted(signal);
    res = await buildRequest();
  }

  // Workspace auth blip: refresh the token to pull in current workspace roles /
  // resource_access (the same cure a manual page reload provides), then retry
  // with backoff. Kept silent — the UI keeps its loading / last-good state
  // instead of showing an error.
  if (await isRecoverableWorkspaceAuthBlip(res)) {
    await tryRefreshSession(); // best-effort; a transient refresh failure just falls through to retries
    throwIfAborted(signal);
    res = await buildRequest();
    let attempt = 0;
    while (attempt < ROLES_BLIP_RETRY_DELAYS_MS.length && (await isRecoverableWorkspaceAuthBlip(res))) {
      await abortableDelay(ROLES_BLIP_RETRY_DELAYS_MS[attempt], signal);
      attempt += 1;
      throwIfAborted(signal);
      res = await buildRequest();
    }
  }

  return res;
}
